/** A puzzle the service asks a browser to solve before another sign-in attempt (`CHALLENGE_REQUIRED`) */
export interface Challenge {
	puzzle: string;
	bits: number;
	expires: string;
}

/**
 * Solves the service's sign-in puzzle: a nonce such that SHA-256(`puzzle:nonce`) has `bits` leading
 * zero bits. It costs a person's browser well under a second at the service's default, and yields to
 * the page every few hundred tries so the screen keeps answering.
 */
export class Solver {
	#digest: (text: string) => Promise<Uint8Array>;

	constructor(digest?: (text: string) => Promise<Uint8Array>) {
		this.#digest = digest ?? (async text => new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))));
	}

	/** Leading zero bits of a digest */
	static zeros(digest: Uint8Array): number {
		let count = 0;
		for (const byte of digest) {
			if (byte === 0) {
				count += 8;
				continue;
			}
			return count + Math.clz32(byte) - 24;
		}
		return count;
	}

	/**
	 * @param signal Stops the search when the person leaves
	 * @returns The solution the service expects with the next attempt
	 */
	async solve(challenge: Challenge, signal?: AbortSignal): Promise<{ puzzle: string; nonce: string }> {
		for (let nonce = 0; ; nonce++) {
			if (signal?.aborted) throw new Error('The challenge was abandoned');
			if (nonce % 500 === 499) await new Promise(done => setTimeout(done, 0));
			if (Solver.zeros(await this.#digest(`${challenge.puzzle}:${nonce}`)) >= challenge.bits) return { puzzle: challenge.puzzle, nonce: String(nonce) };
		}
	}
}
